<template>
  <div class="grc-metadata-manager">
    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <h5 class="mb-0">{{ title }}</h5>
                <small class="text-muted"
                  >Cadastros mestre compartilhados pelos módulos GRC.</small
                >
              </div>
              <b-button
                v-if="canCreate"
                size="sm"
                variant="primary"
                @click="openNew"
              >
                {{ newPrefix }} {{ singularName }}
              </b-button>
            </div>
          </template>
          <b-card-body>
            <div class="grc-filters-section">
              <b-form-group class="mb-0 grc-search-filter" label="Pesquisar">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filter"
                    type="search"
                    placeholder="Pesquisar por nome, código ou descrição"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filter" @click="clearFilter">
                      Limpar
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </div>

            <GRCListTable
              :items="pagedItems"
              :fields="fields"
              :busy="loading"
              :page="page"
              :total-rows="totalRows"
              :per-page="limit"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              empty-text="Nenhum cadastro encontrado"
              busy-text="Carregando cadastros..."
              @sort-changed="onSortChanged"
              @page-change="changePage"
            >
              <template #cell(active)="slotData">
                <b-badge
                  :variant="slotData.item.active ? 'success' : 'secondary'"
                >
                  {{ slotData.item.active ? "Ativo" : "Inativo" }}
                </b-badge>
              </template>
              <template #cell(area)="slotData">
                {{ slotData.item.area ? slotData.item.area.name : "-" }}
              </template>
              <template #cell(areas)="slotData">
                {{ formatLinkedItems(slotData.item.areas) }}
              </template>
              <template #cell(scopes)="slotData">
                {{ formatLinkedItems(slotData.item.scopes) }}
              </template>
              <template #cell(actions)="slotData">
                <div class="grc-action-buttons">
                  <b-button
                    v-if="canEdit"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--edit"
                    title="Editar"
                    aria-label="Editar"
                    @click="openEdit(slotData.item)"
                  >
                    <i class="fa fa-edit" aria-hidden="true"></i>
                  </b-button>
                  <b-button
                    v-if="canDelete"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--delete"
                    title="Inativar"
                    aria-label="Inativar"
                    @click="removeItem(slotData.item)"
                  >
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </b-button>
                </div>
              </template>
              <template #empty>
                <div class="text-muted text-center py-5">
                  <i class="fas fa-folder-open fa-2x"></i>
                  <p class="mt-3 mb-0">Nenhum cadastro encontrado</p>
                </div>
              </template>
            </GRCListTable>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <GRCFormSidebar
      id="grc-metadata-sidebar"
      :visible.sync="showSidebar"
      :title="modalTitle"
      size="compact"
      @hidden="resetForm"
    >
      <b-form class="grc-form" @submit.prevent="saveItem">
        <section class="grc-form__section">
          <div class="grc-form__section-header">
            <h3 class="grc-form__section-title">Informações principais</h3>
            <p class="grc-form__section-description">
              Cadastre os dados básicos e a descrição do item mestre.
            </p>
          </div>

          <b-row>
            <b-col cols="12" md="8">
              <b-form-group :label="`${singularName} *`">
                <b-form-input
                  v-model.trim="form.name"
                  :placeholder="namePlaceholder"
                  required
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="Código">
                <b-form-input
                  v-model.trim="form.code"
                  :placeholder="codePlaceholder"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Descrição">
            <b-form-textarea
              v-model.trim="form.description"
              rows="3"
            ></b-form-textarea>
          </b-form-group>
        </section>

        <section
          v-if="resource === 'scopes' || resource === 'projects'"
          class="grc-form__section"
        >
          <div class="grc-form__section-header">
            <h3 class="grc-form__section-title">Relacionamentos</h3>
            <p class="grc-form__section-description">
              Defina os vínculos necessários para escopos e projetos dentro do
              GRC.
            </p>
          </div>

          <b-row v-if="resource === 'scopes'">
            <b-col cols="12">
              <b-form-group label="Área relacionada">
                <b-form-select
                  v-model="form.area"
                  :options="areaOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <div v-if="resource === 'projects'">
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group label="Áreas vinculadas">
                  <SearchableSelect
                    v-model="form.areas"
                    :options="projectAreaOptions"
                    multiple
                    placeholder="Selecione uma ou mais áreas"
                    search-placeholder="Buscar área..."
                    :use-company-filter="false"
                    :control-class="'form-control'"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Escopos vinculados">
                  <SearchableSelect
                    v-model="form.scopes"
                    :options="scopeOptions"
                    multiple
                    placeholder="Selecione um ou mais escopos"
                    search-placeholder="Buscar escopo..."
                    :use-company-filter="false"
                    :control-class="'form-control'"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="grc-form__toggle-hint">
            Defina se o cadastro permanece disponível para uso nos demais
            módulos GRC.
          </div>
          <b-form-checkbox
            v-model="form.active"
            switch
            class="grc-form__toggle"
          >
            Cadastro ativo
          </b-form-checkbox>
        </section>

        <section v-else class="grc-form__section">
          <div class="grc-form__section-header">
            <h3 class="grc-form__section-title">Status</h3>
            <p class="grc-form__section-description">
              Controle a disponibilidade desse cadastro nos fluxos do módulo.
            </p>
          </div>

          <div class="grc-form__toggle-hint">
            Defina se o cadastro permanece disponível para uso nos demais
            módulos GRC.
          </div>
          <b-form-checkbox
            v-model="form.active"
            switch
            class="grc-form__toggle"
          >
            Cadastro ativo
          </b-form-checkbox>
        </section>

        <div class="grc-form__actions">
          <b-button variant="outline-secondary" @click="showSidebar = false"
            >Cancelar</b-button
          >
          <b-button type="submit" variant="primary" :disabled="saving">
            {{ saving ? "Salvando..." : "Salvar" }}
          </b-button>
        </div>
      </b-form>
    </GRCFormSidebar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  createGrcMetadata,
  deleteGrcMetadata,
  getGrcMetadata,
  updateGrcMetadata,
} from "@/api/grc-metadata";
import SearchableSelect from "@/components/SearchableSelect.vue";
import GRCListTable from "./components/GRCListTable.vue";
import GRCFormSidebar from "./components/GRCFormSidebar.vue";

export default {
  name: "GRCMetadataManager",
  components: {
    GRCFormSidebar,
    GRCListTable,
    SearchableSelect,
  },
  props: {
    resource: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    singularName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      showSidebar: false,
      filter: "",
      page: 1,
      limit: 10,
      sortBy: "name",
      sortDesc: false,
      items: [],
      relatedAreas: [],
      relatedScopes: [],
      editingId: null,
      form: this.getEmptyForm(),
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    fields() {
      const baseFields = [
        { key: "_id", label: "#", sortable: true },
        {
          key: "name",
          label: this.singularName,
          sortable: true,
          thStyle: { "min-width": "220px !important" },
        },
        {
          key: "code",
          label: "Código",
          sortable: true,
          thStyle: { width: "160px !important" },
        },
        { key: "description", label: "Descrição" },
      ];

      if (this.resource === "scopes") {
        baseFields.push({ key: "area", label: "Área" });
      }

      if (this.resource === "projects") {
        baseFields.push({ key: "areas", label: "Áreas" });
        baseFields.push({ key: "scopes", label: "Escopos" });
      }

      baseFields.push({
        key: "active",
        label: "Status",
        sortable: true,
        thStyle: { width: "140px !important" },
      });
      baseFields.push({
        key: "actions",
        label: "Ações",
        thStyle: { width: "180px !important" },
      });

      return baseFields;
    },
    filteredItems() {
      if (!this.filter) {
        return this.items;
      }

      const term = this.filter.toLowerCase();
      return this.items.filter((item) => {
        return [item.name, item.code, item.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      });
    },
    sortedItems() {
      const items = [...this.filteredItems];
      if (!this.sortBy) {
        return items;
      }

      const direction = this.sortDesc ? -1 : 1;
      return items.sort((left, right) => {
        const leftValue = this.normalizeSortValue(left[this.sortBy]);
        const rightValue = this.normalizeSortValue(right[this.sortBy]);

        if (leftValue < rightValue) {
          return -1 * direction;
        }
        if (leftValue > rightValue) {
          return 1 * direction;
        }
        return 0;
      });
    },
    pagedItems() {
      const start = (this.page - 1) * this.limit;
      return this.sortedItems.slice(start, start + this.limit);
    },
    totalRows() {
      return this.sortedItems.length;
    },
    modalTitle() {
      return this.editingId
        ? `Editar ${this.singularName}`
        : `${this.newPrefix} ${this.singularName}`;
    },
    areaOptions() {
      return [
        { value: null, text: "Selecione uma área" },
        ...this.relatedAreas.map((item) => ({
          value: item._id,
          text: item.code ? `${item.code} - ${item.name}` : item.name,
          selectedText: item.name,
        })),
      ];
    },
    projectAreaOptions() {
      return this.relatedAreas.map((item) => ({
        value: item._id,
        text: item.code ? `${item.code} - ${item.name}` : item.name,
        selectedText: item.name,
      }));
    },
    scopeOptions() {
      return this.relatedScopes.map((item) => ({
        value: item._id,
        text: item.area?.name ? `${item.name} (${item.area.name})` : item.name,
        selectedText: item.name,
      }));
    },
    canCreate() {
      return this.hasAnyGrcPermission("create");
    },
    canEdit() {
      return this.hasAnyGrcPermission("edit");
    },
    canDelete() {
      return this.hasAnyGrcPermission("delete");
    },
    namePlaceholder() {
      if (this.resource === "areas") {
        return "Ex: Tecnologia, Recursos Humanos...";
      }
      if (this.resource === "scopes") {
        return "Ex: Desenvolvimento, Recrutamento...";
      }
      if (this.resource === "projects") {
        return "Ex: Implantação de ERP, Auditoria Anual...";
      }
      return "";
    },
    codePlaceholder() {
      if (this.resource === "areas") {
        return "Ex: TI, RH";
      }
      if (this.resource === "scopes") {
        return "Ex: DEV, REC";
      }
      if (this.resource === "projects") {
        return "Ex: PROJ-ERP, AUD-2026";
      }
      return "";
    },
    newPrefix() {
      return this.singularName.toLowerCase() === "área" ||
        this.singularName.toLowerCase() === "area"
        ? "Nova"
        : "Novo";
    },
  },
  watch: {
    resource: {
      immediate: true,
      handler() {
        this.page = 1;
        this.loadAll();
      },
    },
    filter() {
      this.page = 1;
    },
    totalRows() {
      const lastPage = Math.max(Math.ceil(this.totalRows / this.limit), 1);
      if (this.page > lastPage) {
        this.page = lastPage;
      }
    },
  },
  methods: {
    normalizeSortValue(value) {
      if (value === null || value === undefined) {
        return "";
      }
      if (typeof value === "object") {
        return String(
          value.name || value.code || value._id || ""
        ).toLowerCase();
      }
      return String(value).toLowerCase();
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
      this.page = 1;
    },
    changePage(page) {
      this.page = page;
    },
    clearFilter() {
      this.filter = "";
    },
    getEmptyForm() {
      return {
        name: "",
        code: "",
        description: "",
        area: null,
        areas: [],
        scopes: [],
        active: true,
      };
    },
    hasAnyGrcPermission(permission) {
      const role = this.profile.role;
      if (role === "company-root" || role === "root") {
        return true;
      }

      if (parseInt(role) === role) {
        return ["requirements", "risks", "controls", "actionPlans"].some(
          (module) => this.permissions?.[module]?.[permission]
        );
      }

      return false;
    },
    formatLinkedItems(items) {
      if (!items || items.length === 0) {
        return "-";
      }
      return items.map((item) => item.name).join(", ");
    },
    async loadAll() {
      this.loading = true;
      try {
        const requests = [
          getGrcMetadata(this.resource, { includeInactive: true }),
        ];
        if (this.resource === "scopes" || this.resource === "projects") {
          requests.push(getGrcMetadata("areas"));
        }
        if (this.resource === "projects") {
          requests.push(getGrcMetadata("scopes"));
        }

        const responses = await Promise.all(requests);
        this.items = responses[0].data;
        this.relatedAreas = responses[1] ? responses[1].data : [];
        this.relatedScopes = responses[2] ? responses[2].data : [];
      } catch (error) {
        this.showError(
          error,
          `Não foi possível carregar ${this.title.toLowerCase()}`
        );
      } finally {
        this.loading = false;
      }
    },
    openNew() {
      this.editingId = null;
      this.form = this.getEmptyForm();
      this.showSidebar = true;
    },
    openEdit(item) {
      this.editingId = item._id;
      this.form = {
        name: item.name || "",
        code: item.code || "",
        description: item.description || "",
        area: item.area?._id || item.area || null,
        areas: Array.isArray(item.areas)
          ? item.areas.map((entry) => entry._id || entry)
          : [],
        scopes: Array.isArray(item.scopes)
          ? item.scopes.map((entry) => entry._id || entry)
          : [],
        active: item.active !== false,
      };
      this.showSidebar = true;
    },
    resetForm() {
      this.form = this.getEmptyForm();
      this.editingId = null;
    },
    async saveItem() {
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          areas: this.form.areas || [],
          scopes: this.form.scopes || [],
        };

        if (this.editingId) {
          await updateGrcMetadata(this.resource, this.editingId, payload);
        } else {
          await createGrcMetadata(this.resource, payload);
        }

        this.$bvToast.toast(`${this.singularName} salvo com sucesso.`, {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        this.showSidebar = false;
        await this.loadAll();
      } catch (error) {
        this.showError(
          error,
          `Não foi possível salvar ${this.singularName.toLowerCase()}`
        );
      } finally {
        this.saving = false;
      }
    },
    async removeItem(item) {
      const confirmed = window.confirm(
        `Deseja inativar ${this.singularName.toLowerCase()} ${item.name}?`
      );
      if (!confirmed) {
        return;
      }

      try {
        await deleteGrcMetadata(this.resource, item._id);
        this.$bvToast.toast(`${this.singularName} inativado com sucesso.`, {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadAll();
      } catch (error) {
        this.showError(
          error,
          `Não foi possível inativar ${this.singularName.toLowerCase()}`
        );
      }
    },
    showError(error, fallback) {
      const message =
        error?.response?.data || error?.response?.statusText || fallback;
      this.$bvToast.toast(message, {
        title: "Erro",
        toastClass: "custom-toast custom-toast-error",
        variant: "light",
        solid: true,
        autoHideDelay: 10000,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "./grc-list.scss";
@use "./grc-form.scss";
</style>
